import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { getArticleContent, type BlogArticle } from "@/lib/blogContent";
import { Clock, ArrowRight } from "lucide-react";

interface ArticleCardProps {
  article: BlogArticle;
}



export function ArticleCard({ article }: ArticleCardProps) {
  const { language, t } = useLanguage();
  const content = getArticleContent(article.slug, language);
  if (!content) return null;

  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group block rounded-2xl border border-border/50 bg-card overflow-hidden shadow-soft hover:shadow-lg hover:border-primary/30 transition-all duration-300 hover:-translate-y-1"
    >
      {/* Thumbnail */}
      {article.image && (
        <div className="relative overflow-hidden">
          <img
            src={article.image}
            alt={content.title}
            className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {/* Bottom gradient overlay */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to top, hsl(0 0% 0% / 0.35) 0%, transparent 55%)' }}
            aria-hidden="true"
          />
          {/* Top-left: read time badge */}
          <div className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-card/85 backdrop-blur-sm text-foreground shadow">
            <Clock className="w-3 h-3 text-primary" aria-hidden="true" />
            {article.readTime} {t('blog.minRead')}
          </div>
        </div>
      )}

      <div className="p-5 flex flex-col gap-3">
        {/* Date */}
        <time dateTime={article.date} className="text-xs text-muted-foreground">
          {new Date(article.date).toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' })}
        </time>

        {/* Title */}
        <h2 className="text-lg font-semibold font-serif text-foreground group-hover:text-primary transition-colors leading-snug">
          {content.title}
        </h2>

        {/* Excerpt */}
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
          {content.excerpt}
        </p>

        {/* Read more */}
        <span className="inline-flex items-center gap-1 text-sm font-medium text-primary mt-1 group-hover:gap-2 transition-all">
          {t('blog.readMore')}
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}
